import {
  correctReceiptLine,
  movingCost,
  mulDivRound,
  storedLineAmount,
  typedRatePaisa,
  type CorrectedReceipt,
} from '@ims/core'
import type { RowOf } from '@ims/data'
import { Button, Field, Input, Modal, useToast } from '@ims/ui'
import { useState } from 'react'
import { MoneyInput } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { money } from '../lib/format.ts'
import { rateAgainstSaved, rateLooksOff } from '../lib/rate-check.ts'
import { correctReceiptRate } from '../lib/rows.ts'
import { useRepo, useWorkspace } from '../workspace.tsx'

// Correcting a rate on a saved receipt (docs/plans/receipt-corrections.md). Boards: `rate-fix`.

const byLedgerOrder = (a: RowOf<'stock_movements'>, b: RowOf<'stock_movements'>) =>
  a.occurred_at.localeCompare(b.occurred_at) ||
  a.created_at.localeCompare(b.created_at) ||
  a.id.localeCompare(b.id)

export function CorrectRateDialog({
  receipt,
  lines,
  line,
  productName,
  savedCostPaisa,
  onClose,
}: {
  receipt: RowOf<'goods_receipts'>
  lines: RowOf<'goods_receipt_lines'>[]
  line: RowOf<'goods_receipt_lines'>
  productName: string
  savedCostPaisa: number | null
  onClose: () => void
}) {
  const repo = useRepo()
  const settings = useSettings()
  const { current } = useWorkspace()
  const toast = useToast()
  const was = typedRatePaisa(line)
  const [rate, setRate] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const data = useLive(
    async (r) => {
      const [units, movements, receiptMovements, batches] = await Promise.all([
        r.list('product_units', { where: { product_id: line.product_id }, limit: 1 }),
        r.list('stock_movements', { where: { product_id: line.product_id }, limit: 1_000_000 }),
        r.list('stock_movements', { where: { ref_id: receipt.id }, limit: 10_000 }),
        Promise.all(lines.map((l) => (l.batch_id ? r.get('batches', l.batch_id) : null))),
      ])
      return {
        units: units.rows[0] ?? null,
        movements: movements.rows.toSorted(byLedgerOrder),
        receiptMovements: receiptMovements.rows,
        batches: batches.filter((b) => !!b),
      }
    },
    [line.product_id, receipt.id],
    ['product_units', 'stock_movements', 'batches'],
  ).data
  const typedUnit = line.entered_unit
  const stockUnit = data?.units
    ? data.units.stock_in === 'larger'
      ? data.units.larger_name
      : data.units.smaller_name
    : null
  const per = (name: string | null) => (name ? ` per ${name}` : '')
  const m = (p: number) => money(p, settings)
  const fixed: CorrectedReceipt | null =
    rate && rate > 0 ? correctReceiptLine(lines, receipt, line.id, rate) : null
  const at = lines.indexOf(line)
  // The moving average as it is, and as it would be with this delivery's new cost.
  const average = (costs: (m: RowOf<'stock_movements'>) => number) => {
    if (!data) return null
    const basis = movingCost(
      data.movements.map((x) => ({
        qtyMilli: x.qty_milli,
        unitCostPaisa: costs(x),
        costed: x.type === 'receipt' || x.type === 'opening',
      })),
    )
    return basis ? mulDivRound(basis.cost, 1000, basis.qtyMilli) : null
  }
  const before = fixed?.trueCostsBefore[at] ?? 0
  const after = fixed?.trueCostsAfter[at] ?? 0
  const units = line.qty_milli + line.free_qty_milli
  const own = data?.movements.find(
    (x) =>
      x.ref_id === receipt.id &&
      x.type === 'receipt' &&
      x.batch_id === line.batch_id &&
      x.qty_milli === units,
  )
  const averageNow = average((x) => x.unit_cost_paisa ?? 0)
  const averageThen = average((x) => (x.id === own?.id ? after : (x.unit_cost_paisa ?? 0)))
  const stockRate = fixed?.line.unit_cost_paisa ?? 0
  const looksOff = !!fixed && rateLooksOff(stockRate, savedCostPaisa)

  const save = async () => {
    if (!current || !data || !rate) return
    setBusy(true)
    try {
      await repo.commit(
        correctReceiptRate(
          { tenantId: current.tenant_id },
          {
            receipt,
            lines,
            movements: data.receiptMovements,
            batches: data.batches,
            lineId: line.id,
            ratePaisa: rate,
            reason: reason.trim() || null,
          },
        ),
      )
      toast.success('Rate corrected')
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const change = (label: string, from: number, to: number) => (
    <div className="flex items-baseline justify-between gap-3 border-t border-soft py-1.5">
      <span className="text-muted">{label}</span>
      <span className="tabular whitespace-nowrap">
        <span className="text-subtle">{m(from)} → </span>
        <b className="font-semibold">{m(to)}</b>
      </span>
    </div>
  )
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Correct the rate"
      subtitle={`${productName}${receipt.reference ? `, bill no. ${receipt.reference}` : ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!fixed || !data || rate === was}
            onClick={() => void save()}
          >
            Correct rate
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Field
          label={`Right rate${per(typedUnit)}`}
          hint={`Saved as ${m(was)}${per(typedUnit)}.${
            savedCostPaisa ? ` Your saved cost is ${m(savedCostPaisa)}${per(stockUnit)}.` : ''
          }`}
        >
          {(id) => <MoneyInput id={id} prefix="Rs." autoFocus paisa={rate} onValue={setRate} />}
        </Field>
        {fixed && rate !== was && (
          <div>
            <p className="mb-1 font-medium">What changes</p>
            {change('This line', storedLineAmount(line), fixed.amountPaisa)}
            {before !== after && change(`Cost${per(stockUnit)}`, before, after)}
            {averageNow !== null &&
              averageThen !== null &&
              averageNow !== averageThen &&
              change('Average cost, for profit', averageNow, averageThen)}
            {looksOff && savedCostPaisa && (
              <p className="mt-2 font-medium text-warning">
                {rateAgainstSaved(stockRate, savedCostPaisa)}.
              </p>
            )}
          </div>
        )}
        <p className="text-muted">
          Stock stays as it is. The supplier’s bill stays too; you can fix it next if it is wrong.
        </p>
        <Field label="Reason (optional)">
          {(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
      </div>
    </Modal>
  )
}
